//striped-data-grid.tsx

import { alpha, styled } from "@mui/material";
import {
  DataGrid,
  gridClasses,
  DataGridProps,
  GridToolbar,
  GridColumnMenu,
  useGridApiRef,
} from "@mui/x-data-grid";
import * as React from "react";

// 1️⃣ Base styled DataGrid with striping etc.
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}`]: {
    cursor: "pointer",
  },
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        0.2 + theme.palette.action.selectedOpacity,
      ),
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          0.2 +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity,
        ),
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            0.2 + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  },
  "& .archived-row": {
    opacity: 0.6,
    fontStyle: "italic",
  },
  "& .archived-icon-cell": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 !important",
  },
  "& .MuiDataGrid-cell .cell-text": {
    display: "inline-block",
    paddingRight: "2px",
    fontStyle: "inherit",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
}));

type StripedDataGridProps = DataGridProps & {
  storageKey?: string;
  columnLayoutVersion?: number;
};

// 2️⃣ Wrapper that ONLY adds defaults, but respects showToolbar / slots / slotProps
export const StripedDataGrid = React.forwardRef<
  HTMLDivElement,
  StripedDataGridProps
>(function StripedDataGrid(props, ref) {
  const {
    getRowClassName,
    getRowId,
    slots,
    slotProps,
    storageKey,
    columnLayoutVersion,
    columns,
    ...rest
  } = props;
  // default row class
  const mergedGetRowClassName =
    getRowClassName ??
    ((p) => (p.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"));

  // default row id
  const mergedGetRowId = getRowId ?? ((row: any) => row.id);
  const apiRef = useGridApiRef();
  const defaultColumnLayout = React.useRef({
    orderedFields: columns.map((column) => column.field),
    columnVisibilityModel: rest.initialState?.columns?.columnVisibilityModel,
  });
  const mergedColumns = React.useMemo(() => {
    if (!storageKey) {
      return columns;
    }

    const saved = window.localStorage.getItem(storageKey);

    if (!saved) {
      return columns;
    }

    try {
      const parsed = JSON.parse(saved);
      const dimensions = parsed?.columns?.dimensions;

      if (!dimensions) {
        return columns;
      }

      return columns.map((column) => {
        const width = dimensions[column.field]?.width;

        if (!width) {
          return column;
        }

        return {
          ...column,
          width,
        };
      });
    } catch {
      return columns;
    }
  }, [columns, storageKey]);

  React.useEffect(() => {
    if (!storageKey) {
      return;
    }

    const saved = window.localStorage.getItem(storageKey);

    if (!saved) {
      return;
    }

    try {
      const parsed = JSON.parse(saved);

      // Reset obsolete column layouts without discarding saved widths,
      // sorting, filters, or pagination. Subsequent user choices still persist.
      if (
        columnLayoutVersion !== undefined &&
        parsed.columnLayoutVersion !== columnLayoutVersion
      ) {
        parsed.columns = {
          ...parsed.columns,
          ...defaultColumnLayout.current,
        };
      }

      const initialRestore = setTimeout(() => {
        apiRef.current?.restoreState(parsed);
      }, 0);

      const settledRestore = setTimeout(() => {
        apiRef.current?.restoreState(parsed);
      }, 250);

      return () => {
        clearTimeout(initialRestore);
        clearTimeout(settledRestore);
      };
    } catch {
      // Ignore invalid saved state
    }
  }, [apiRef, storageKey, columnLayoutVersion]);

  const saveGridState = React.useCallback(() => {
    if (!storageKey) {
      return;
    }

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        ...apiRef.current?.exportState(),
        columnLayoutVersion,
      }),
    );
  }, [apiRef, storageKey, columnLayoutVersion]);

  React.useEffect(() => {
    if (!storageKey) {
      return;
    }

    const unsubscribeStateChange = apiRef.current?.subscribeEvent(
      "stateChange",
      saveGridState,
    );

    const unsubscribeColumnWidthChange = apiRef.current?.subscribeEvent(
      "columnWidthChange",
      saveGridState,
    );

    return () => {
      unsubscribeStateChange?.();
      unsubscribeColumnWidthChange?.();
    };
  }, [apiRef, saveGridState, storageKey]);

  // default slots: only supply toolbar if user didn't
  const mergedSlots = {
    toolbar: GridToolbar,
    columnMenu: GridColumnMenu, // ✅ restore default
    ...slots,
  };

  // merge toolbar props, but DO NOT interfere with showToolbar
  const mergedSlotProps = {
    ...slotProps,
    toolbar: {
      showQuickFilter: true,
      csvOptions: {
        valueFormatter: true,
        allColumns: true,
        fileName: "seeds",
        ...(slotProps?.toolbar?.csvOptions ?? {}),
      },
      printOptions: {
        disableToolbarButton: true,
        ...(slotProps?.toolbar?.printOptions ?? {}),
      },
      ...(slotProps?.toolbar ?? {}),
    },
  };

  return (
    <StyledDataGrid
      apiRef={apiRef}
      ref={ref}
      {...rest}
      columns={mergedColumns}
      disableColumnMenu={false}
      getRowClassName={mergedGetRowClassName}
      getRowId={mergedGetRowId}
      slots={mergedSlots}
      slotProps={mergedSlotProps}
    />
  );
});
